"use client"

import { useEffect, useState } from "react"
import { GlassFold } from "./GlassFold"

export function ThemedBackground() {
  const [isDark, setIsDark] = useState<boolean>(() => {
    if (typeof document === "undefined") return true
    return (
      document.documentElement.classList.contains("dark") ||
      !document.documentElement.classList.contains("light")
    )
  })

  useEffect(() => {
    const updateTheme = () => {
      const isDarkMode =
        document.documentElement.classList.contains("dark") ||
        !document.documentElement.classList.contains("light")
      setIsDark(isDarkMode)
    }

    updateTheme()

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === "attributes" && m.attributeName === "class") {
          updateTheme()
        }
      }
    })

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    })

    return () => {
      observer.disconnect()
    }
  }, [])

  // Dark Mode vs Light Mode configurations specified by GradFlow guidelines
  const theme = isDark
    ? {
        color1: "#9a512f",
        color2: "#140d0a",
        color3: "#f2b48c",
        speed: 0.4,
        scale: 0.9,
        noise: 0.08,
        vignette: 1,
        baseColor: "#1a1310",
        fallbackGradient:
          "radial-gradient(ellipse 80% 60% at 50% 20%, #9a512f 0%, #140d0a 80%)",
        overlay:
          "linear-gradient(to bottom, rgba(26,19,16,0.10), rgba(26,19,16,0.35))",
      }
    : {
        color1: "#f2b98f",
        color2: "#e8d8c5",
        color3: "#fff7ee",
        speed: 0.3,
        scale: 0.9,
        noise: 0.05,
        vignette: 0.1,
        baseColor: "#f5ede3",
        fallbackGradient:
          "radial-gradient(ellipse 80% 60% at 50% 20%, #f2b98f 0%, #e8d8c5 80%)",
        overlay:
          "linear-gradient(to bottom, rgba(245,237,227,0.20), rgba(245,237,227,0.50))",
      }

  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 0,
        pointerEvents: "none",
        overflow: "hidden",
        backgroundColor: theme.baseColor,
      }}
    >
      {/* Static CSS gradient fallback behind canvas */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: theme.fallbackGradient,
          backgroundColor: theme.baseColor,
          transition: "background 0.5s ease",
        }}
      />

      {/* Animated WebGL Canvas */}
      <div
        style={{
          position: "absolute",
          inset: 0,
        }}
      >
        <GlassFold
          color1={theme.color1}
          color2={theme.color2}
          color3={theme.color3}
          speed={theme.speed}
          scale={theme.scale}
          noise={theme.noise}
          vignette={theme.vignette}
        />
      </div>

      {/* Thin readability overlay div above canvas */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: theme.overlay,
          transition: "background 0.5s ease",
        }}
      />
    </div>
  )
}
